import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image, { type ImageProps } from 'next/image';
import type { ReactElement, ReactNode } from 'react';
import styles from './PageHero.module.scss';
import { minWidth } from '../tokens/breakpoints';

/**
 * `dark`: the hero on the dark surface, a band (a band directly after it
 * abuts it). The hero keeps no space below it: the next section starts one
 * section-rhythm run (P) below its edge.
 */
export type PageHeroSurface = 'default' | 'dark';
/**
 * `flush`: on a `Split` from lg the `placement="split"` media column runs
 * 80px past the hero's bottom edge, into the space the page's band keeps
 * under the hero, so a photo meets the bottom of that band and the copy
 * column keeps the space (O222); below lg nothing changes.
 */
export type PageHeroMediaSpacing = 'default' | 'flush';
export interface PageHeroProps {
  children: ReactNode;
  mediaSpacing?: PageHeroMediaSpacing;
  surface?: PageHeroSurface;
}
export type PageHeroContainerWidth = 'default' | 'full';
export interface PageHeroContainerProps {
  children: ReactNode;
  width?: PageHeroContainerWidth;
}
export interface PageHeroSplitProps {
  children: ReactNode;
  /**
   * The split sits inside the default (gutter) `Container`, not the
   * full-width one: its column already starts at the container's content
   * edge, so the `placement="split"` copy starts at its column's start at
   * every width, with no inset, no start padding and no auto centring: on
   * the header logo's edge from lg (the gutter, or the centred container's
   * content edge past its max width), and on the stacked media's edge below
   * lg (no side padding there). The text measure stays 560px; the vertical
   * padding is unchanged (Phase 6, /employers).
   */
  contained?: boolean;
  mediaFirstOnMobile?: boolean;
}

/**
 * `split`: the copy column of a `Split`. From lg it starts at the page gutter
 * (the centred container's content edge past its max width), which assumes
 * the `Split` sits in `Container width="full"`; inside the default container
 * the gutter would be counted twice, so a `Split` there is `contained`.
 */
export type PageHeroContentPlacement = 'plain' | 'lead' | 'split';
export type PageHeroContentTone = 'default' | 'inverse';
export interface PageHeroContentProps {
  children: ReactNode;
  placement?: PageHeroContentPlacement;
  tone?: PageHeroContentTone;
}

/**
 * The media slot's height. `auto`: it fills its column (the split's row from
 * lg, the viewport's height from the wide breakpoint); below lg it is capped
 * at 600px (400px below xs) and clips what is taller, for a photo banner.
 * `floating`: an 800px banner with no cap, for a hero overlay. `content`: as
 * `auto` from lg; below lg the slot takes its content's height, with no cap
 * and nothing clipped, for content that sets its own height (a framed Media
 * card with its ratio, or a page-sized photo box).
 */
export type PageHeroMediaHeight = 'auto' | 'floating' | 'content';
export type PageHeroMediaOverlay = 'none' | 'scrim' | 'leftFade';
/**
 * `split`: the media column of a `Split`. `bleed` (incubating): the
 * redesign's 50/50 hero media, a rounded frame (16px, 32px from md) that runs
 * to the viewport's end edge beside split content, for a hero under the
 * transparent SiteHeader tone; below lg it stacks inside the gutters.
 */
export type PageHeroMediaPlacement = 'block' | 'split' | 'bleed';
export interface PageHeroMediaProps {
  children: ReactNode;
  height?: PageHeroMediaHeight;
  overlay?: PageHeroMediaOverlay;
  placement?: PageHeroMediaPlacement;
}
export type PageHeroImageFrame = 'fill' | 'portrait' | 'square';
export interface PageHeroImageProps {
  alt: string;
  frame?: PageHeroImageFrame;
  mobileSrc?: ImageProps['src'];
  priority?: boolean;
  sizes?: string;
  src: ImageProps['src'];
}
export interface PageHeroCaptionProps {
  children: ReactNode;
}
export interface PageHeroOverlayProps {
  children: ReactNode;
}
export type PageHeroComponent = ((props: PageHeroProps) => ReactElement) & {
  Caption: typeof PageHeroCaption;
  Container: typeof PageHeroContainer;
  Content: typeof PageHeroContent;
  Image: typeof PageHeroImage;
  Media: typeof PageHeroMedia;
  Overlay: typeof PageHeroOverlay;
  Split: typeof PageHeroSplit;
};
const DEFAULT_IMAGE_SIZES: Record<PageHeroImageFrame, string> = {
  fill: '100vw',
  portrait: `${minWidth('lg')} 50vw, 100vw`,
  square: `${minWidth('lg')} 50vw, 100vw`
};
function PageHeroRoot(t0) {
  const $ = _c(8);
  const {
    children,
    mediaSpacing: t1,
    surface: t2
  } = t0;
  const mediaSpacing = t1 === undefined ? "default" : t1;
  const surface = t2 === undefined ? "default" : t2;
  const t3 = surface === "dark" ? "flush" : undefined;
  const t4 = surface === "dark" ? "dark" : undefined;
  const t5 = styles[`helios-page-hero--surface-${surface}`];
  const t6 = mediaSpacing === "flush" && styles["helios-page-hero--media-spacing-flush"];
  let t7;
  if ($[0] !== t5 || $[1] !== t6) {
    t7 = classNames(styles["helios-page-hero"], t5, t6);
    $[0] = t5;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== children || $[4] !== t3 || $[5] !== t4 || $[6] !== t7) {
    t8 = <section data-helios-band={t3} data-helios-section="" data-helios-surface={t4} className={t7}>{children}</section>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  return t8;
}
function PageHeroContainer(t0) {
  const $ = _c(5);
  const {
    children,
    width: t1
  } = t0;
  const width = t1 === undefined ? "default" : t1;
  const t2 = styles[`helios-page-hero__container--${width}`];
  let t3;
  if ($[0] !== t2) {
    t3 = classNames(styles["helios-page-hero__container"], t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function PageHeroSplit(t0) {
  const $ = _c(6);
  const {
    children,
    contained: t1,
    mediaFirstOnMobile: t2
  } = t0;
  const contained = t1 === undefined ? false : t1;
  const mediaFirstOnMobile = t2 === undefined ? true : t2;
  const t3 = contained && styles["helios-page-hero__split--contained"];
  const t4 = mediaFirstOnMobile && styles["helios-page-hero__split--media-first-mobile"];
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = classNames(styles["helios-page-hero__split"], t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== children || $[4] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[3] = children;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
function PageHeroContent(t0) {
  const $ = _c(6);
  const {
    children,
    placement: t1,
    tone: t2
  } = t0;
  const placement = t1 === undefined ? "plain" : t1;
  const tone = t2 === undefined ? "default" : t2;
  const t3 = styles[`helios-page-hero__content--placement-${placement}`];
  const t4 = styles[`helios-page-hero__content--tone-${tone}`];
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = classNames(styles["helios-page-hero__content"], t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== children || $[4] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[3] = children;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
function PageHeroMedia(t0) {
  const $ = _c(11);
  const {
    children,
    height: t1,
    overlay: t2,
    placement: t3
  } = t0;
  const height = t1 === undefined ? "auto" : t1;
  const overlay = t2 === undefined ? "none" : t2;
  const placement = t3 === undefined ? "block" : t3;
  const t4 = styles[`helios-page-hero__banner--height-${height}`];
  const t5 = overlay !== "none" && styles[`helios-page-hero__banner--overlay-${overlay}`];
  let t6;
  if ($[0] !== t4 || $[1] !== t5) {
    t6 = classNames(styles["helios-page-hero__banner"], t4, t5);
    $[0] = t4;
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== children || $[4] !== t6) {
    t7 = <div className={t6}>{children}</div>;
    $[3] = children;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const banner = t7;
  if (placement !== "block") {
    const t8 = styles[`helios-page-hero__media--placement-${placement}`];
    let t9;
    if ($[6] !== t8) {
      t9 = classNames(styles["helios-page-hero__media"], t8);
      $[6] = t8;
      $[7] = t9;
    } else {
      t9 = $[7];
    }
    let t10;
    if ($[8] !== banner || $[9] !== t9) {
      t10 = <div className={t9}>{banner}</div>;
      $[8] = banner;
      $[9] = t9;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    return t10;
  }
  return banner;
}
function PageHeroImage(t0) {
  const $ = _c(19);
  const {
    alt,
    frame: t1,
    mobileSrc,
    priority,
    sizes,
    src
  } = t0;
  const frame = t1 === undefined ? "fill" : t1;
  const hasMobileImage = Boolean(mobileSrc);
  const t2 = styles[`helios-page-hero__image-frame--${frame}`];
  const t3 = hasMobileImage && styles["helios-page-hero__image-frame--has-mobile"];
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = classNames(styles["helios-page-hero__image-frame"], t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = sizes ?? DEFAULT_IMAGE_SIZES[frame];
  const t6 = hasMobileImage && styles["helios-page-hero__image--desktop"];
  let t7;
  if ($[3] !== t6) {
    t7 = classNames(styles["helios-page-hero__image"], t6);
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== alt || $[6] !== priority || $[7] !== src || $[8] !== t5 || $[9] !== t7) {
    t8 = <Image src={src} alt={alt} fill={true} priority={priority} sizes={t5} className={t7} />;
    $[5] = alt;
    $[6] = priority;
    $[7] = src;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== alt || $[12] !== mobileSrc || $[13] !== priority) {
    t9 = mobileSrc && <Image src={mobileSrc} alt={alt} fill={true} priority={priority} sizes="100vw" className={classNames(styles["helios-page-hero__image"], styles["helios-page-hero__image--mobile"])} />;
    $[11] = alt;
    $[12] = mobileSrc;
    $[13] = priority;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t4 || $[16] !== t8 || $[17] !== t9) {
    t10 = <div className={t4}>{t8}{t9}</div>;
    $[15] = t4;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function PageHeroCaption(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className={styles["helios-page-hero__caption"]}>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function PageHeroOverlay(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className={styles["helios-page-hero__overlay"]}>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const PageHero = Object.assign(PageHeroRoot, {
  Caption: PageHeroCaption,
  Container: PageHeroContainer,
  Content: PageHeroContent,
  Image: PageHeroImage,
  Media: PageHeroMedia,
  Overlay: PageHeroOverlay,
  Split: PageHeroSplit
}) as PageHeroComponent;
export default PageHero;